import React from "react"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { 
  Clock, 
  Users, 
  Bed, 
  AlertTriangle, 
  Shield, 
  FileText, 
  Heart,
  Baby,
  Flame,
  AlertCircle
} from "lucide-react"
import SEO from "../SEO"

export default function CitizensCharter() {
  const citizensCharterSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Citizen's Charter - Pranaam Hospitals",
    "description": "Pranaam Hospitals Citizen's Charter outlining our services, standards, and commitment to patient care quality.",
    "url": "https://pranaam.pranaamhospitals.in/patient-care/citizens-charter"
  }

  return (
    <>
      <SEO
        title="Citizen's Charter | Pranaam Hospitals - Patient Care Standards"
        description="Learn about Pranaam Hospitals' Citizen's Charter, our service standards, patient rights, and commitment to quality healthcare delivery."
        canonical="https://pranaam.pranaamhospitals.in/patient-care/citizens-charter"
        schema={citizensCharterSchema}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary/10 to-primary/5 py-16">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Patient Care</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
                Citizen's <span className="text-primary">Charter</span>
              </h1>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                This citizens charter seeks to provide a framework that enables our customers to know what services are available in this hospital, the quality of services they are entitled to, and the means through which complaints regarding denial or poor quality of service will be redressed.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto space-y-8">
              
              {/* Standards of Services */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-6 h-6 text-primary" />
                    Standards of Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    This is a Multi-Specialty Hospital Accredited by both NABH and NABL. It provides medical care to all patients who come to the hospital. We insist that all our customers receive courteous and prompt attention.
                  </p>
                </CardContent>
              </Card>

              {/* General Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    General Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                      <h3 className="font-semibold text-lg">Doctors</h3>
                      <p className="text-gray-600">55 approx. (including Resident doctors)</p>
                    </div>
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                      <h3 className="font-semibold text-lg">Nurses</h3>
                      <p className="text-gray-600">120 approx. (including supervisors)</p>
                    </div>
                    <div className="text-center p-4 bg-primary/5 rounded-lg">
                      <Bed className="w-8 h-8 text-primary mx-auto mb-2" />
                      <h3 className="font-semibold text-lg">Beds</h3>
                      <p className="text-gray-600">136 (functional 60 beds)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Services NOT Available */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    Services NOT Available at Pranaam Hospital
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    There is no facility for cardiac surgeries, Cardiology, Organ Transplant, Oncology, Radio Therapy, chemotherapy, nuclear medicine, and interventional radiology as of date.
                  </p>
                </CardContent>
              </Card>

              {/* Casualty and Emergency Services */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-primary" />
                    Casualty and Emergency Services
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 text-primary font-semibold">
                    <Clock className="w-5 h-5" />
                    24 hours, all days
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Casualty Medical Officer available 24 hours all days</li>
                    <li>• Duty Doctor available round the clock in major specialties: Gynecology and Obstetrics, Pediatrics, Medicine, Surgery, Orthopedics, and ENT</li>
                    <li>• Consultant Doctors are available on call after their OPD hours</li>
                    <li>• Emergency cases are attended to promptly</li>
                    <li>• In serious cases, treatment/management gets priority over paperwork</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Out-Patient Departments */}
              <Card>
                <CardHeader>
                  <CardTitle>Out-Patient Departments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Morning OPD</h4>
                      <p className="text-blue-700">9:00 AM to 2:00 PM (Except Sundays)</p>
                      <p className="text-sm text-blue-600">Registration up to 12:30 PM</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">Evening OPD</h4>
                      <p className="text-green-700">6:00 PM to 8:30 PM</p>
                      <p className="text-sm text-green-600">Registration up to 8:00 PM</p>
                    </div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-2">Pediatric OPD (Sundays)</h4>
                    <p className="text-orange-700">9:00 AM to 12:00 noon</p>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Every Out Patient is registered and PHUID is generated</li>
                    <li>• List of consultants displayed at reception area near ER department</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Laboratory and X-ray */}
              <Card>
                <CardHeader>
                  <CardTitle>Laboratory and X-ray</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-primary font-semibold mb-4">
                    <Clock className="w-5 h-5" />
                    24 hours, all days
                  </div>
                  <p className="text-gray-700">Reports are made available within the TAT as specified.</p>
                </CardContent>
              </Card>

              {/* Miscellaneous Facilities */}
              <Card>
                <CardHeader>
                  <CardTitle>Miscellaneous Facilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Wheel chairs and stretchers available on request at casualty gate</li>
                    <li>• Lift available for access to different floors</li>
                    <li>• Ambulance services available 24/7 on payment</li>
                    <li>• Two stand-by generators for emergency services</li>
                    <li>• Adequate drinking water and toilet facilities</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Indoor Treatment */}
              <Card>
                <CardHeader>
                  <CardTitle>Indoor Treatment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">
                    The hospital has general ward, sharing rooms, private non-AC rooms, deluxe rooms, and Pranaam Exclusive for admitting patients.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Morning Visiting Hours</h4>
                      <p className="text-blue-700">7:00 AM to 9:00 AM</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">Evening Visiting Hours</h4>
                      <p className="text-green-700">5:00 PM to 7:00 PM</p>
                      <p className="text-sm text-green-600">Maximum 2 visitors per patient</p>
                    </div>
                  </div>
                  <p className="text-gray-700">
                    • All patients get one patient/attendant pass<br/>
                    • Only one attendant allowed to stay with patient
                  </p>
                </CardContent>
              </Card>

              {/* Emergency Codes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    Emergency Codes Policy
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-blue-800">CODE BLUE</h4>
                      </div>
                      <p className="text-blue-700">Cardiac emergency</p>
                    </div>
                    <div className="p-4 bg-pink-50 rounded-lg border-l-4 border-pink-500">
                      <div className="flex items-center gap-2 mb-2">
                        <Baby className="w-5 h-5 text-pink-600" />
                        <h4 className="font-semibold text-pink-800">CODE PINK</h4>
                      </div>
                      <p className="text-pink-700">Child abduction</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                      <div className="flex items-center gap-2 mb-2">
                        <Flame className="w-5 h-5 text-red-600" />
                        <h4 className="font-semibold text-red-800">CODE RED</h4>
                      </div>
                      <p className="text-red-700">All fire emergencies</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <h4 className="font-semibold text-orange-800">CODE ORANGE</h4>
                      </div>
                      <p className="text-orange-700">Internal and external disasters</p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-semibold text-yellow-800 mb-2">During Code Announcements:</h4>
                    <ul className="text-yellow-700 space-y-1">
                      <li>• Patients and attendants should vacate ramp areas and corridors</li>
                      <li>• Stay in your respective rooms or wards</li>
                      <li>• Do not move out until the code is relaxed</li>
                      <li>• Do not panic - rescue team will help you to safer areas</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Complaints and Grievances */}
              <Card>
                <CardHeader>
                  <CardTitle>Complaints and Grievances</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Complaint register is kept at reception</li>
                    <li>• Every grievance will be duly acknowledged</li>
                    <li>• We aim to settle genuine complaints within 3 working days</li>
                    <li>• Suggestions box is provided at the front office</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Important Guidelines */}
              <Card className="border-l-4 border-primary">
                <CardHeader>
                  <CardTitle className="text-primary">Important Guidelines</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Hospital is a NO-SMOKING zone</li>
                    <li>• Management is not responsible for any thefts - keep valuables in your custody</li>
                    <li>• Beware of touts</li>
                    <li>• Please use hospital facilities with care</li>
                    <li>• Help us keep the hospital and surroundings clean</li>
                    <li>• Please consider the convenience of other patients</li>
                  </ul>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </div>
    </>
  )
}